import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "redux/store";
import {
  setOrgIdWithRelRTK,
  clearOrgIdWithRelRTK,
} from "redux/slices/orgIdWithRelRTKSlice";
import {
  useGetProfileQuery,
} from "redux/services/membership";
import { getOrganisationMembership } from "utils";
import { useRoles } from "hooks/useRoles";
import { GetOrganisationRelationshipsResponse } from "redux/services/membership/types";

interface UsePayrollOwnerOrgIdResult {
  orgIdWithRel: string;
  selectedOrgIdWithRelName: string;
  listOrganizationOptions: {
    value: string;
    label: string;
  }[];
  selectedOrgIdWithRelCode: string;
  dataGetOrgRel: { data: GetOrganisationRelationshipsResponse["data"] } | undefined;
  pspSPC: string;
  setOrgIdWithRel: (orgId: string) => Promise<void>;
  clearOrgIdWithRel: () => void;
}

export const useOrgIdWithRel = (): UsePayrollOwnerOrgIdResult => {
  const dispatch = useDispatch();
  const orgIdWithRel = useSelector(
    (state: RootState) => state.orgIdWithRelRTK.orgIdWithRel
  );

  const {
    isPSPMakerCheckerRole,
    isPSPMakerRole,
    isLoading: isLoadingUseRoles,
    orgRelData,
    isFetchingOrgRel,
  } = useRoles();

  const { data: profileData, isFetching: isFetchingGetProfile } =
    useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  // reuse orgRelData from useRoles — no second fetch needed
  const dataGetOrgRel = { data: orgRelData };

  const selectedOrgIdWithRelName = useMemo(() => {
    if (!isPSPMakerCheckerRole) return "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.companyName ?? "";
  }, [dataGetOrgRel?.data, isPSPMakerCheckerRole, orgIdWithRel]);

  const selectedOrgIdWithRelCode = useMemo(() => {
    if (!isPSPMakerCheckerRole) return "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.leiNumber ?? "";
  }, [dataGetOrgRel?.data, isPSPMakerCheckerRole, orgIdWithRel]);

  const listOrganizationOptions = useMemo(
    () =>
      dataGetOrgRel?.data?.map((item) => ({
        value: item?.ownerOrg.id ?? "",
        label: item?.ownerOrg.companyName ?? item?.ownerOrg.tradingName ?? "",
      })).filter((item) => item.value && item.label) ?? [],
    [dataGetOrgRel]
  );

  const pspSPC = useMemo(() => {
    if (!isPSPMakerRole) return "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.companyNumber ?? "";
  }, [dataGetOrgRel?.data, isPSPMakerRole, orgIdWithRel]);

  useEffect(() => {
    if (isFetchingOrgRel || isFetchingGetProfile || isLoadingUseRoles)
      return;

    const current = membershipOrganisation?.organisationId ?? "";

    // only reset orgIdWithRel to PSP's own id when the user is genuinely not a PSP
    // Maker/Checker role. Never overwrite a valid BU selection — this guards against
    // isPSPMakerCheckerRole briefly flickering false during re-renders.
    const isValidBUSelection =
      orgRelData.length > 0 &&
      orgRelData.some((item) => item.ownerOrg.id === orgIdWithRel);

    if (!isPSPMakerCheckerRole && !isValidBUSelection && current && current !== orgIdWithRel) {
      dispatch(setOrgIdWithRelRTK({ orgIdWithRel: current }));
    }
  }, [
    dispatch,
    isPSPMakerCheckerRole,
    membershipOrganisation?.organisationId,
    orgIdWithRel,
    isFetchingOrgRel,
    isFetchingGetProfile,
    isLoadingUseRoles,
    orgRelData,
  ]);

  const setOrgIdWithRel = (newOrgId: string): Promise<void> => {
    dispatch(setOrgIdWithRelRTK({ orgIdWithRel: newOrgId }));
    return Promise.resolve();
  };

  const clearOrgIdWithRel = (): void => {
    dispatch(clearOrgIdWithRelRTK());
  };

  return {
    orgIdWithRel,
    setOrgIdWithRel,
    clearOrgIdWithRel,
    selectedOrgIdWithRelName,
    listOrganizationOptions,
    selectedOrgIdWithRelCode,
    dataGetOrgRel,
    pspSPC,
  };
};
